import { useCallback, useEffect, useRef } from 'react';
import { SPELL_REFRESH_EVENT } from '../utils/spellRefreshEvents';

export const SPELL_SYNC_FALLBACK_MS = 30000;

// Socket events provide prompt updates; periodic reads recover missed events.
export default function useCampaignSpellSync({ campaignId, kind, load, onData }) {
  const apply = useRef(onData); apply.current = onData;
  const control = useRef(null);
  const refresh = useCallback(() => control.current?.refresh() || Promise.resolve(), []);
  useEffect(() => {
    if (!campaignId) return undefined;
    let alive = true; let pending = false; let running = null; let debounce;
    const poll = () => {
      if (!alive || document.hidden) return Promise.resolve();
      if (running) { pending = true; return running; }
      running = (async () => {
        do {
          pending = false;
          try {
            const data = await load();
            if (alive) apply.current(data);
          } catch (_) { /* Retain visible state and retry on the next event or fallback. */ }
        } while (alive && pending && !document.hidden);
      })().finally(() => { running = null; });
      return running;
    };
    const schedule = event => {
      if (event?.type === SPELL_REFRESH_EVENT &&
          (event.detail?.campaignId !== campaignId || !event.detail?.[kind])) return;
      clearTimeout(debounce);
      debounce = setTimeout(poll, 100);
    };
    const visible = () => { if (!document.hidden) schedule(); };
    const controller = { refresh: poll }; control.current = controller;
    poll();
    const timer = setInterval(() => { if (!running) poll(); }, SPELL_SYNC_FALLBACK_MS);
    window.addEventListener(SPELL_REFRESH_EVENT, schedule);
    if (kind === 'web') window.addEventListener('web:changed', schedule);
    window.addEventListener('focus', visible);
    document.addEventListener('visibilitychange', visible);
    return () => {
      alive = false; clearTimeout(debounce); clearInterval(timer);
      if (control.current === controller) control.current = null;
      window.removeEventListener(SPELL_REFRESH_EVENT, schedule);
      if (kind === 'web') window.removeEventListener('web:changed', schedule);
      window.removeEventListener('focus', visible);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [campaignId, kind, load]);
  return refresh;
}
